import React, { useEffect, useState } from 'react';
import { Progress } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';

import { VALIDATION_POLL_INTERVAL_MS } from '~constants/common';

import { AlertList, ModalCard, Spinner } from '~components';
import { AlertMessage } from '~components/form-fields/inputs';

import { useGetCutoverRunQuery } from '~support-at-home/actions/sahServiceCutover';

interface ComponentProps {
  runId: string;
  /** Told whether the run is still going, so the modal can refuse to close while it is. */
  onRunningChange?: (isRunning: boolean) => void;
}

/**
 * A budget run, while it is happening and once it is done.
 *
 * The work outlives the request that starts it - a measured two thousand budgets takes around forty
 * seconds - so the browser is handed a run id and polls it. Showing progress rather than a spinner
 * matters at that length: a provider needs to see it moving to know it has not died, and a run that
 * IS dead is reported as failed by the server rather than spinning here forever.
 *
 * A status that cannot be read at all is its own third state, reported rather than mistaken for
 * progress. Polling stops only once the server reports a terminal state.
 */
const CutoverRunProgress: React.FC<ComponentProps> = ({ runId, onRunningChange }) => {
  const { data: run, error, refetch } = useGetCutoverRunQuery({ run_id: runId }, { refetchOnMountOrArgChange: true });

  // Open. This is the only thing on the step while the run is going - a collapsed card would show a
  // provider a closed header and no sign that anything is happening.
  const [isCardOpen, setIsCardOpen] = useState(true);

  // Only when nothing has come back at all. A failed poll after a good one keeps the last status in
  // `data`, and a blip is not worth pulling the progress out from under someone mid-run.
  const cannotReadStatus = Boolean(error) && !run;

  // Terminal means the server said so. An unreadable status is NOT running as far as the modal is
  // concerned - reporting it as running is what leaves a provider shut inside a spinner with no way
  // out but a page reload, on the one screen meant to tell them whether their budgets finished.
  const isRunning = !cannotReadStatus && (run ? run.status === 'running' : true);

  useEffect(() => {
    onRunningChange?.(isRunning);
  }, [isRunning, onRunningChange]);

  // Kept going through a failure rather than stopped on it: the run itself carries on server-side, so
  // a recovered endpoint should put the provider back on the progress rather than leave them looking
  // at an error about a run that has since finished.
  const isSettled = Boolean(run) && run?.status !== 'running';
  useEffect(() => {
    if (isSettled) return undefined;

    const interval = setInterval(() => refetch(), VALIDATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isSettled, refetch]);

  if (cannotReadStatus) {
    return (
      <AlertMessage
        id="cutover-run-unavailable"
        name="cutover-run-unavailable"
        value={`The progress of this cutover could not be read: ${getErrorMessage(error)}. The run itself carries on - open the assistant again to pick it back up.`}
        type="alert-danger"
        color="danger"
      />
    );
  }

  if (!run) return <Spinner loading />;

  const percent = run.total > 0 ? Math.round((run.processed / run.total) * 100) : 0;

  if (run.status === 'running') {
    return (
      <ModalCard
        header={`Bringing ${run.total} budgets into line`}
        className="w-100 mb-3"
        isOpen={isCardOpen}
        setIsOpen={setIsCardOpen}
      >
        {/* Named for a screen reader. The bar's visible "25%" is its content, not its name, and the
            role sits on the inner element - so the name has to be pointed at something, and
            `barAriaLabelledBy` is the only prop that reaches that element. */}
        <span id="cutover-run-progress-label" className="visually-hidden">
          Budget cutover progress
        </span>
        <Progress value={percent} striped animated barAriaLabelledBy="cutover-run-progress-label">
          {percent}%
        </Progress>
        <div className="mt-3">
          {run.processed} of {run.total} processed &middot; {run.lines_split} line
          {run.lines_split === 1 ? '' : 's'} split &middot; {run.lines_moved} moved
        </div>
        <div className="mt-2 text-muted">
          This takes a few moments. Please stay on this page until it finishes. If you do lose the page, the run keeps
          going - open the assistant again and it will bring you back here.
        </div>
      </ModalCard>
    );
  }

  const done = [
    `${run.updated} budget${run.updated === 1 ? '' : 's'} brought into line: ${run.lines_split} line${run.lines_split === 1 ? '' : 's'} split at the cutover and ${run.lines_moved} moved across whole.`,
  ];

  if (run.updated > 0) {
    done.push(
      'Each of those budgets is flagged Needs Review in the budgets list. Open it, check it and save it - saving clears the flag.',
    );
  }

  return (
    <div>
      {run.status === 'failed' && (
        <AlertMessage
          id="cutover-run-failed"
          name="cutover-run-failed"
          value={run.error_message ?? 'The cutover did not finish.'}
          type="alert-danger"
          color="danger"
        />
      )}

      <AlertList
        header={run.status === 'failed' ? 'Before it stopped, this run applied:' : 'This run applied the following:'}
        color={run.status === 'failed' ? 'warning' : 'success'}
        listItems={done}
      />

      {run.failed_count > 0 && (
        <ModalCard
          header={`${run.failed_count} budget${run.failed_count === 1 ? '' : 's'} could not be brought into line`}
          className="w-100 mb-3"
          isOpen={isCardOpen}
          setIsOpen={setIsCardOpen}
        >
          <ul className="mb-0">
            {run.failures.map(({ budget_id, reference, reason }) => (
              <li key={budget_id}>
                {reference}: {reason}
              </li>
            ))}
          </ul>
          {/* The stored list is capped, so a run that failed on everything does not write a list as
              long as itself into its own row. The count above is always exact. */}
          {run.failed_count > run.failures.length && (
            <div className="mt-2 text-muted">
              Showing the first {run.failures.length}. Start the assistant again to see what is still outstanding.
            </div>
          )}
        </ModalCard>
      )}
    </div>
  );
};

export default CutoverRunProgress;
